import { useCallback, useState } from 'react'
import { VIEW_MODES, normalizeViewMode } from '../utils/viewMode'

/**
 * View mode remembered per list in localStorage.
 *
 * Deliberately the same shape as usePersistedSortMode in SortSelect.jsx, and
 * for the same reasons: normalizeViewMode() guards the read so a stale key
 * from an older build (or a hand-edited value) can't leave a tab in a mode no
 * renderer draws, and both accesses are wrapped because localStorage throws in
 * private-mode Safari and when a browser blocks storage outright. A view
 * preference is not worth taking the tab down for.
 */
export function usePersistedViewMode(storageKey) {
  const [mode, setMode] = useState(() => {
    try {
      return normalizeViewMode(localStorage.getItem(storageKey))
    } catch {
      return normalizeViewMode(null)
    }
  })

  const update = useCallback(
    (next) => {
      const valid = normalizeViewMode(next)
      setMode(valid)
      try {
        localStorage.setItem(storageKey, valid)
      } catch {
        // Preference just won't survive the reload.
      }
    },
    [storageKey]
  )

  return [mode, update]
}

export default function ViewToggle({ value, onChange, label = 'View mode' }) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{
        display: 'inline-flex',
        // Matched to the sort select and filter input beside it: same border
        // color, same radius, same resulting height, so the toolbar keeps one
        // baseline across all three controls.
        border: '1px solid var(--accent-teal)',
        borderRadius: '8px',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {VIEW_MODES.map((mode, i) => {
        const active = value === mode.id
        return (
          <button
            key={mode.id}
            type="button"
            aria-pressed={active}
            title={`${mode.label} view`}
            onClick={() => onChange(mode.id)}
            style={{
              background: active ? 'var(--accent-teal)' : 'var(--bg-secondary)',
              color: active ? 'var(--text-light)' : 'var(--text-muted)',
              border: 'none',
              borderLeft: i > 0 ? '1px solid var(--accent-teal)' : 'none',
              padding: '0.75rem 0.85rem',
              fontSize: '0.95rem',
              fontFamily: 'inherit',
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'background 0.2s ease, color 0.2s ease',
            }}
          >
            <span aria-hidden="true">{mode.icon}</span>
            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{mode.label}</span>
          </button>
        )
      })}
    </div>
  )
}
